import { ConfirmDialog, Field, Textarea, titleCase, toast } from '@westhetics/ui'
import { useId, useState, type ReactNode } from 'react'
import { errorMessage, fieldErrors } from '@/lib/errors'
import {
  type Channels,
  type RowAction,
  type SignatureProvider,
  type SignatureRequest,
  useCancelSignature,
  useDeleteSignedDocument,
  useResendSignature,
  useSignAsClinic,
  useUndoSignatureAction,
} from './api'
import { ChannelPicker } from './channel-picker'
import {
  canceledToast,
  copyText,
  documentsWord,
  firstName,
  firstOpenChannel,
  resentToast,
  whatsappHref,
} from './labels'

/**
 * Reenviar, cancelar, assinar como responsável e excluir (root), do jeito que a lista, o painel e a aba
 * Documentos da ficha usam (RN-ASS-09, 21, 24, 25). Enviar e reenviar têm Desfazer por 10 segundos no
 * aviso (RN-ASS-14); assinar e excluir confirmam antes. No WhatsApp assistido (P-ASS-21), depois do
 * reenvio a mensagem fica pronta para a pessoa mandar pelo WhatsApp dela.
 */

export interface ActionTarget {
  appointmentId: string
  patientName: string
  contact: { whatsapp: string | null; email: string | null }
  /** O status derivado: decide se cancelar pede motivo (a paciente já assinou). */
  status: string
  actions: RowAction[]
  documentCount: number
  clinicName?: string
  /** Quem conduz o envio: na Autentique, a clínica assina lá, pelo token dela (RN-ASS-37). */
  provider?: SignatureProvider
}

export interface DeleteTarget extends ActionTarget {
  document: { id: string; title: string }
}

/** A mensagem pronta para a clínica mandar à mão (P-ASS-21). */
export interface ManualMessage {
  patientName: string
  whatsapp: string | null
  text: string
  link: string
}

export function manualMessageOf(
  request: SignatureRequest,
  patientName: string,
): ManualMessage | null {
  if (request.provider === 'autentique') return null
  if (request.whatsappMode !== 'manual' || !request.link || !request.whatsappText) return null
  if (request.channels === 'email') return null
  return {
    patientName,
    whatsapp: request.recipient.whatsapp,
    text: request.whatsappText,
    link: request.link,
  }
}

export function useSignatureActions(
  options: { whatsappMode?: 'provider' | 'manual'; provider?: SignatureProvider } = {},
) {
  const whatsappMode = options.whatsappMode ?? 'manual'
  const provider = options.provider ?? 'westhetics'
  const resendMutation = useResendSignature()
  const cancelMutation = useCancelSignature()
  const undoMutation = useUndoSignatureAction()
  const signMutation = useSignAsClinic()
  const deleteMutation = useDeleteSignedDocument()

  const [resending, setResending] = useState<{ target: ActionTarget; channels: Channels } | null>(
    null,
  )
  const [canceling, setCanceling] = useState<{ target: ActionTarget; reason: string } | null>(null)
  const [signing, setSigning] = useState<ActionTarget | null>(null)
  const [deleting, setDeleting] = useState<{ target: DeleteTarget; justification: string } | null>(
    null,
  )
  const [manual, setManual] = useState<ManualMessage | null>(null)
  const reasonId = useId()

  const undo = (actionId: string) => () => {
    undoMutation.mutate(actionId, {
      onSuccess: () => toast('Desfeito. Nada saiu.'),
      onError: (error) => toast(errorMessage(error)),
    })
  }

  function askResend(target: ActionTarget) {
    setResending({ target, channels: firstOpenChannel(target.contact) })
  }

  function confirmResend() {
    if (!resending) return
    const { target, channels } = resending
    resendMutation.mutate(
      { appointmentId: target.appointmentId, channels },
      {
        onSuccess: (result) => {
          setResending(null)
          toast(resentToast(result.patientFirstName, channels), { undo: undo(result.actionId) })
          const message = manualMessageOf(result.signatureRequest, result.patientName)
          if (message) setManual(message)
        },
        onError: (error) => toast(errorMessage(error)),
      },
    )
  }

  function askCancel(target: ActionTarget) {
    setCanceling({ target, reason: '' })
  }

  const cancelNeedsReason = canceling?.target.status === 'awaiting_clinic_signature'

  function confirmCancel() {
    if (!canceling) return
    const { target, reason } = canceling
    if (cancelNeedsReason && reason.trim().length < 10) return
    cancelMutation.mutate(
      {
        appointmentId: target.appointmentId,
        reason: cancelNeedsReason ? reason.trim() : undefined,
      },
      {
        onSuccess: (result) => {
          setCanceling(null)
          toast(canceledToast(result.patientFirstName), { undo: undo(result.actionId) })
        },
        onError: (error) => toast(errorMessage(error)),
      },
    )
  }

  function askSign(target: ActionTarget) {
    setSigning(target)
  }

  function confirmSign() {
    if (!signing) return
    signMutation.mutate(signing.appointmentId, {
      onSuccess: () => {
        setSigning(null)
        toast('Assinado. Está tudo registrado.')
      },
      onError: (error) => toast(errorMessage(error)),
    })
  }

  function askDelete(target: DeleteTarget) {
    setDeleting({ target, justification: '' })
  }

  function confirmDelete() {
    if (!deleting) return
    const { target, justification } = deleting
    if (justification.trim().length < 10) return
    deleteMutation.mutate(
      {
        appointmentId: target.appointmentId,
        documentId: target.document.id,
        justification: justification.trim(),
      },
      {
        onSuccess: () => {
          setDeleting(null)
          toast(
            `'${target.document.title}' de ${titleCase(target.patientName)} excluído. O registro de prova permanece na trilha.`,
          )
        },
        onError: (error) => toast(errorMessage(error)),
      },
    )
  }

  async function copyLink(link: string) {
    toast(
      (await copyText(link))
        ? 'Link copiado.'
        : 'Não foi possível copiar. Selecione o endereço e copie com o teclado.',
    )
  }

  async function copyMessage(text: string) {
    toast(
      (await copyText(text))
        ? 'Mensagem copiada. Cole no WhatsApp da paciente.'
        : 'Não foi possível copiar. Selecione o texto e copie com o teclado.',
    )
  }

  function openWhatsapp(message: ManualMessage) {
    if (!message.whatsapp) return
    window.open(whatsappHref(message.whatsapp, message.text), '_blank', 'noopener')
  }

  const cancelReasonError =
    canceling && cancelNeedsReason && canceling.reason.trim().length < 10 && canceling.reason !== ''
      ? 'Diga o motivo em pelo menos 10 caracteres.'
      : (fieldErrors(cancelMutation.error).reason ?? null)
  const deleteError =
    deleting && deleting.justification.trim().length < 10 && deleting.justification !== ''
      ? 'Diga o motivo em pelo menos 10 caracteres.'
      : (fieldErrors(deleteMutation.error).justification ?? null)

  const dialogs: ReactNode = (
    <>
      <ConfirmDialog
        open={resending !== null}
        onOpenChange={(open) => {
          if (!open && !resendMutation.isPending) setResending(null)
        }}
        title={`Reenviar o link para ${resending ? titleCase(resending.target.patientName) : ''}?`}
        effect="O link é reemitido com os mesmos documentos e o mesmo protocolo. Nenhum documento novo é criado; a leitura que ela já fez continua valendo."
        confirmLabel="Reenviar"
        cancelLabel="Voltar"
        tone="primary"
        loading={resendMutation.isPending}
        loadingLabel="Reenviando…"
        onConfirm={confirmResend}
      >
        {resending ? (
          <ChannelPicker
            value={resending.channels}
            onChange={(channels) => setResending({ ...resending, channels })}
            contact={resending.target.contact}
            whatsappMode={whatsappMode}
            provider={resending.target.provider ?? provider}
            label="Reenviar por"
          />
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={canceling !== null}
        onOpenChange={(open) => {
          if (!open && !cancelMutation.isPending) setCanceling(null)
        }}
        title={`Cancelar o envio para ${canceling ? titleCase(canceling.target.patientName) : ''}?`}
        effect={`O link deixa de funcionar e ${canceling ? documentsWord(canceling.target.documentCount) : 'os documentos'} ficam no histórico como cancelados. Nada assinado se desfaz; o atendimento e o plano continuam.`}
        alternative={
          canceling && canceling.target.actions.includes('resend')
            ? {
                description:
                  'Alternativa: reenviar o link. A paciente recebe um link novo e os documentos continuam os mesmos.',
                label: 'Reenviar',
                onSelect: () => {
                  const target = canceling.target
                  setCanceling(null)
                  askResend(target)
                },
              }
            : undefined
        }
        confirmLabel="Cancelar envio"
        cancelLabel="Voltar"
        loading={cancelMutation.isPending}
        loadingLabel="Cancelando…"
        onConfirm={confirmCancel}
      >
        {canceling && cancelNeedsReason ? (
          <Field
            id={reasonId}
            label="Motivo"
            hint="A paciente já assinou: o motivo fica na trilha e ela recebe o aviso por e-mail."
            error={cancelReasonError}
          >
            <Textarea
              id={reasonId}
              rows={3}
              value={canceling.reason}
              onChange={(event) => setCanceling({ ...canceling, reason: event.target.value })}
            />
          </Field>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={signing !== null}
        onOpenChange={(open) => {
          if (!open && !signMutation.isPending) setSigning(null)
        }}
        title="Assinar como responsável pela clínica?"
        effect={`Você assina ${signing ? documentsWord(signing.documentCount) : 'os documentos'} de ${signing ? titleCase(signing.patientName) : ''}${signing?.clinicName ? ` em nome da ${signing.clinicName}` : ''}. ${
          (signing?.provider ?? provider) === 'autentique'
            ? 'A assinatura é feita na Autentique, com a conta da clínica, e fica registrada com seu nome, data e hora.'
            : 'Fica registrado com seu nome, data, hora e IP.'
        }`}
        confirmLabel="Assinar"
        cancelLabel="Voltar"
        tone="primary"
        loading={signMutation.isPending}
        loadingLabel="Assinando…"
        onConfirm={confirmSign}
      />

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) setDeleting(null)
        }}
        title={`Excluir '${deleting?.target.document.title ?? ''}' de ${deleting ? titleCase(deleting.target.patientName) : ''}?`}
        effect="O documento some da ficha, das listas e dos downloads. O registro de prova (protocolo, hash, assinaturas, IP) permanece na trilha e não pode ser apagado. Nada volta."
        confirmLabel="Excluir"
        cancelLabel="Voltar"
        loading={deleteMutation.isPending}
        loadingLabel="Excluindo…"
        onConfirm={confirmDelete}
      >
        {deleting ? (
          <Field id={`${reasonId}-justification`} label="Justificativa" error={deleteError}>
            <Textarea
              id={`${reasonId}-justification`}
              rows={3}
              value={deleting.justification}
              onChange={(event) => setDeleting({ ...deleting, justification: event.target.value })}
            />
          </Field>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={manual !== null}
        onOpenChange={(open) => {
          if (!open) setManual(null)
        }}
        title={`Mande o link para ${manual ? firstName(manual.patientName) : ''} pelo seu WhatsApp`}
        effect="O WhatsApp ainda não sai pelo sistema: a mensagem abaixo já está pronta, com o link. Abra o WhatsApp com ela ou copie e cole."
        alternative={
          manual
            ? {
                description: `Alternativa: copiar a mensagem e colar na conversa com ${firstName(manual.patientName)}.`,
                label: 'Copiar mensagem',
                onSelect: () => void copyMessage(manual.text),
              }
            : undefined
        }
        confirmLabel={manual?.whatsapp ? 'Abrir WhatsApp' : 'Copiar link'}
        cancelLabel="Fechar"
        tone="primary"
        onConfirm={() => {
          if (!manual) return
          if (manual.whatsapp) openWhatsapp(manual)
          else void copyLink(manual.link)
          setManual(null)
        }}
      >
        {manual ? (
          <pre className="border-hairline-strong bg-surface font-body text-ink max-h-[200px] overflow-auto border px-[14px] py-3 text-[12px] leading-[1.6] font-light whitespace-pre-wrap">
            {manual.text}
          </pre>
        ) : null}
      </ConfirmDialog>
    </>
  )

  return {
    askResend,
    askCancel,
    askSign,
    askDelete,
    copyLink,
    copyMessage,
    openWhatsapp,
    showManual: setManual,
    undo,
    isBusy:
      resendMutation.isPending ||
      cancelMutation.isPending ||
      signMutation.isPending ||
      deleteMutation.isPending,
    dialogs,
  }
}
